import { useEffect } from 'react';
import { trackPerformance } from '@/lib/analytics-exports';
import { computeClsSession, describeShiftSource, type LayoutShiftLike } from '@/lib/clsAttribution';

/**
 * Report this page view's CLS, and name the element that caused it.
 *
 * WHY THIS EXISTS. CrUX puts the homepage at CLS 0.94 (p75, mobile, measured
 * 2026-08-20 via PageSpeed Insights) against a 0.25 "poor" threshold, and the
 * origin at 0.84 with zero good URLs. Five lab traces at 390x844 / 4x CPU /
 * Fast 4G — including two in a fresh context with no stored consent, and
 * including the homepage itself — all measured 0.00, with a known-positive
 * control confirming the observer could see an injected shift. So the field
 * number is real and unreproducible here, which means guessing at it is the
 * one thing guaranteed not to work.
 *
 * WHAT WAS ALREADY THERE, AND WHY IT COULD NOT ANSWER THIS. usePerformanceObserver
 * has watched `layout-shift` since before this, and it is mounted on exactly
 * one route — /playlists, the signed-in library, which is noindex and does not
 * appear in the top 30 organic pages. The homepage had no observer at all. It
 * also recorded `sources.length`, a COUNT, so even where it ran it could say a
 * shift happened but never which element moved. Both gaps are the reason five
 * lab runs were the available evidence.
 *
 * REPORTS ONCE PER PAGE VIEW, NOT PER SHIFT. The existing hook fires an
 * analytics event on every entry, which on a genuinely shifting page means
 * dozens of events per view through dataLayer and first-party both. CLS is
 * defined as the worst SESSION WINDOW anyway, so a per-entry stream cannot be
 * summed into the metric downstream — it has to be windowed here and sent once.
 */
export function WebVitalsReporter() {
  useEffect(() => {
    if (typeof PerformanceObserver === 'undefined') return;
    if (!PerformanceObserver.supportedEntryTypes?.includes('layout-shift')) return;

    const entries: LayoutShiftLike[] = [];
    let observer: PerformanceObserver;
    try {
      observer = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) entries.push(e as unknown as LayoutShiftLike);
      });
      // `buffered` matters: hydration happens well after the first paint, and
      // the shifts that hurt most are the early ones this component was not
      // mounted for yet.
      observer.observe({ type: 'layout-shift', buffered: true });
    } catch {
      return;
    }

    // THE LANDING PATH, captured at mount — not the path at report time.
    //
    // This component sits outside <Routes>, so it mounts once per browser page
    // load and survives every SPA navigation. Reading window.location.pathname
    // when the report fires therefore labels the whole session with whatever
    // page the user happened to END on.
    //
    // Measured 2026-08-21, and it silently corrupted the first day of data:
    // /generate showed p75 CLS 1.000 and the homepage showed max 0.078 across 5
    // views, while CrUX put the homepage at 0.95 mobile / 0.79 desktop. The
    // homepage is where people LAND and /generate is where they GO, so every
    // landing session's shifts were being filed under the destination.
    //
    // CrUX attributes a page load to the URL that was loaded, and CLS is a
    // per-page-load metric, so capturing at mount is also the unit that makes
    // this beacon comparable to the CrUX figure it exists to explain.
    const landingPath = window.location.pathname;

    let reported = false;
    const report = () => {
      if (reported) return;
      reported = true;
      try {
        const session = computeClsSession(entries);
        trackPerformance('cls', Number(session.value.toFixed(4)), {
          // The whole point of the beacon: which element moved.
          culprit: session.culprit ? describeShiftSource(session.culprit) : 'none',
          shift_count: session.count,
          largest_shift: Number(session.largest.toFixed(4)),
          path: landingPath,
          // Kept so a session that roamed is still identifiable: when these
          // differ, the CLS covers more than one view and the culprit may not
          // live on the landing page.
          exit_path: window.location.pathname,
          viewport_width: window.innerWidth,
        });
      } catch {
        // A telemetry beacon must never be able to break a page unload.
      }
    };

    // visibilitychange -> hidden is the reliable end-of-view signal; pagehide
    // is the iOS Safari backstop, where a tab can be frozen without ever
    // firing visibilitychange (design-rules section 10).
    const onHide = () => { if (document.visibilityState === 'hidden') report(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', report);

    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', report);
      observer.disconnect();
      // A SPA route change unmounts this before either event fires, and that
      // view's shifts are still a real page view worth attributing.
      report();
    };
  }, []);

  return null;
}
